import React, { useEffect, useState } from 'react';
import API from '../services/api';
import { formatDate } from '../dateUtils';

/**
 * AdminDebtRecordsPage (patched)
 * -------------------------------------------------------------
 * This version switches the data source and mutations from the
 * daily-report endpoints to the dedicated debt-records endpoints.
 *
 * • GET    /admin/debt-records
 * • PUT    /admin/debt-records/:recordId  { status, notes }
 *
 * Using these keeps debt_records.status authoritative so the
 * therapist-side list (which shows only OPEN) updates correctly
 * when you mark items SUBMITTED/PAID here.
 */
export default function AdminDebtRecordsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Track which row is in "notes edit" mode
  const [editingId, setEditingId] = useState(null);
  // Local notes edits per row
  const [notesMap, setNotesMap] = useState({});

  useEffect(() => {
    void fetchRows();
  }, []);

  async function fetchRows() {
    try {
      setLoading(true);
      setError('');
      // NEW SOURCE OF TRUTH
      const res = await API.get('/admin/debt-records');
      const data = Array.isArray(res.data) ? res.data : [];
      setRows(data);
    } catch (e) {
      console.error(e);
      setError('Error fetching debt records.');
    } finally {
      setLoading(false);
    }
  }

  async function saveNotes(row) {
    try {
      const notes = notesMap[row.id] ?? row.notes ?? '';
      await API.put(`/admin/debt-records/${row.id}`, { status: row.debt_status || 'OPEN', notes });
      setEditingId(null);
      setNotesMap((m) => {
        const { [row.id]: _omit, ...rest } = m;
        return rest;
      });
      await fetchRows();
      alert('Notes saved.');
    } catch (e) {
      console.error(e);
      alert('Error saving notes.');
    }
  }

  async function changeStatus(row, newStatus) {
    try {
      const notes = notesMap[row.id] ?? row.notes ?? '';
      await API.put(`/admin/debt-records/${row.id}`, { status: newStatus, notes });
      await fetchRows();
      // Intentionally no alert spam; the table will update.
    } catch (e) {
      console.error(e);
      alert('Error updating status.');
    }
  }

  return (
    <div style={{ padding: '1rem' }}>
      <h2>Debt Records</h2>

      {error && (
        <div style={{ color: 'red', marginBottom: '0.75rem' }}>{error}</div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <button onClick={fetchRows} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {loading ? (
        <div>Loading…</div>
      ) : rows.length === 0 ? (
        <div>No debt records found.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Client</th>
              <th style={th}>Therapist</th>
              <th style={th}>Session Date</th>
              <th style={th}>Billed</th>
              <th style={th}>Collected</th>
              <th style={th}>Owed</th>
              <th style={th}>Debt Status</th>
              <th style={th}>Therapist Notes</th>
              <th style={th}>Admin Notes</th>
              <th style={th}>Actions</th>
              <th style={th}>Change Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const billed = parseFloat(row.billed_amount) || 0;
              const collected = parseFloat(row.collected_amount) || 0;
              const owed = billed - collected;
              const isEditing = editingId === row.id;

              return (
                <tr key={row.id}>
                  <td style={td}>{row.client_name}</td>
                  <td style={td}>
                    {row.therapist_first_name} {row.therapist_last_name}
                  </td>
                  <td style={td}>{row.session_date ? formatDate(row.session_date) : ''}</td>
                  <td style={td}>${billed.toFixed(2)}</td>
                  <td style={td}>${collected.toFixed(2)}</td>
                  <td style={td}>${owed.toFixed(2)}</td>
                  <td style={td}>{row.debt_status}</td>
                  <td style={td}>{row.therapist_notes || ''}</td>
                  <td style={td}>
                    {isEditing ? (
                      <textarea
                        rows={3}
                        value={notesMap[row.id] ?? row.notes ?? ''}
                        onChange={(e) => setNotesMap((m) => ({ ...m, [row.id]: e.target.value }))}
                        style={{ width: '100%' }}
                      />
                    ) : (
                      row.notes || ''
                    )}
                  </td>
                  <td style={td}>
                    {isEditing ? (
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <button onClick={() => saveNotes(row)}>Save</button>
                        <button
                          onClick={() => {
                            setEditingId(null);
                            setNotesMap((m) => {
                              const { [row.id]: _omit, ...rest } = m;
                              return rest;
                            });
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setEditingId(row.id);
                          setNotesMap((m) => ({ ...m, [row.id]: row.notes || '' }));
                        }}
                      >
                        Add/Edit Notes
                      </button>
                    )}
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button onClick={() => changeStatus(row, 'OPEN')}>Keep as Debt</button>
                      <button onClick={() => changeStatus(row, 'SUBMITTED')}>Mark Submitted</button>
                      <button onClick={() => changeStatus(row, 'PAID')}>Mark Paid</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

const th: React.CSSProperties = {
  textAlign: 'left',
  borderBottom: '1px solid var(--border-color)',
  padding: '8px',
};

const td: React.CSSProperties = {
  borderBottom: '1px solid var(--surface-muted)',
  padding: '8px',
};
